import { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import { colors } from '../../utils/colors';
import { useBotProtection } from '../../utils/bot-protection';

const NotificationWrapper = styled(motion.div)`
  position: fixed;
  bottom: 2rem;
  left: 2rem;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  background: ${props => {
    if (props.type === 'error') return colors.error;
    if (props.type === 'info') return colors.primary;
    return colors.success;
  }};
  color: ${colors.white};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  i {
    font-size: 1.2rem;
  }
`;

// Generic email service configuration
const emailConfig = {
  serviceID: process.env.REACT_APP_EMAILJS_SERVICE_ID,
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
  templates: {
    contact: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
    consultation: process.env.REACT_APP_EMAILJS_CONSULTATION_TEMPLATE_ID,
  }
};

// Option mappings for converting values to human-readable labels
const serviceOptionsMap = {
  'presencial': 'Presencial',
  'online': 'Online'
};

const timeOptionsMap = {
  'morning': 'Mañana (9:00 - 12:00)',
  'afternoon': 'Tarde (13:00 - 17:00)',
  'evening': 'Noche (18:00 - 20:00)'
};

// Generic email sending function
const sendEmail = async (templateId, templateParams) => {
  try {
    const { serviceID, publicKey } = emailConfig;

    if (!serviceID || !templateId || !publicKey) {
      return {
        success: false,
        message: 'Error de configuración. Por favor, contacta al administrador.'
      };
    }

    // Send email using EmailJS
    const result = await emailjs.send(
      serviceID,
      templateId,
      templateParams,
      publicKey
    );

    if (result.status === 200) {
      return {
        success: true,
        message: '¡Mensaje enviado con éxito! Te contactaré pronto.'
      };
    } else {
      throw new Error('Failed to send email');
    }
  } catch (error) {
    return {
      success: false,
      message: 'Hubo un error al enviar el mensaje. Por favor, intenta nuevamente.'
    };
  }
};

// Contact form handler
export const sendContactEmail = async (formData) => {
  const templateParams = {
    from_name: formData.name,
    from_email: formData.email,
    message: formData.message,
  };
  
  return sendEmail(emailConfig.templates.contact, templateParams);
};

// Consultation scheduling form handler
export const sendConsultationEmail = async (formData) => {
  const templateParams = {
    from_name: formData.name,
    from_email: formData.email,
    phone: formData.phone || 'No proporcionado',
    preferred_time: timeOptionsMap[formData.preferredTime] || formData.preferredTime || 'No especificada',
    consultation_type: serviceOptionsMap[formData.consultationType] || formData.consultationType || 'General',
    message: formData.message || '',
    form_type: 'Agenda de Consulta'
  };
  
  return sendEmail(emailConfig.templates.consultation, templateParams);
};

export const Notification = ({ message, type, onClose }) => {
  return (
    <NotificationWrapper
      type={type}
      initial={{ opacity: 0, y: 50, x: -50 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, y: 50, x: -50 }}
      transition={{ type: 'spring', damping: 20 }}
    >
      <i className={
        type === 'error' ? 'fas fa-exclamation-circle' :
        type === 'info' ? 'fas fa-info-circle' :
        'fas fa-check-circle'
      } />
      {message}
    </NotificationWrapper>
  );
};

export const useEmailService = (formId = 'default') => {
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const botProtection = useBotProtection(formId);

  const submitEmail = async (formData, emailHandler = sendContactEmail) => {
    // Bot protection validation
    const validation = botProtection.validateSubmission(formData);
    
    if (!validation.allowed) {
      // For rate limiting, show as info/success message instead of error
      const isRateLimit = ['session_limit', 'daily_limit', 'cooldown', 'rate_limit'].includes(validation.reason);
      
      setNotification({
        message: validation.message,
        type: isRateLimit ? 'info' : 'error'
      });
      
      // Auto-hide notification after longer time for rate limit messages
      setTimeout(() => {
        setNotification(null);
      }, isRateLimit ? 12000 : 8000); // Longer display for acknowledgment messages
      
      return { 
        success: false, 
        message: validation.message,
        blocked: true,
        reason: validation.reason,
        isRateLimit: isRateLimit,
        shouldClearForm: isRateLimit // Clear form for rate limit messages
      };
    }

    setLoading(true);
    const result = await emailHandler(formData);
    setLoading(false);

    setNotification({
      message: result.message,
      type: result.success ? 'success' : 'error'
    });

    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      setNotification(null);
    }, 5000);

    return result;
  };

  // Specific methods for different forms
  const submitContactForm = (formData) => submitEmail(formData, sendContactEmail);
  const submitConsultationForm = (formData) => submitEmail(formData, sendConsultationEmail);

  return {
    submitEmail,
    submitContactForm,
    submitConsultationForm,
    loading,
    notification,
    clearNotification: () => setNotification(null),
    // Bot protection utilities
    botProtection: {
      honeypotField: botProtection.honeypotField,
      recordActivity: botProtection.recordActivity,
      getRemainingSubmissions: botProtection.getRemainingSubmissions,
      getFormTime: botProtection.getFormTime,
      isRateLimited: botProtection.isRateLimited
    }
  };
};